import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useMemo, useRef } from 'react';
import { getDeliveryOrPickupByVehicle } from '@/SearchResults/ResultTile/hooks';
import { maybeHasGreatDealTag } from '~/components/SearchResults/ResultTile/ResultTile.helpers';
import { useResponseErrors } from '~/contexts/ErrorContext';
import { useUserDeliveryInfo } from '~/contexts/InventoryContext';
import { Vehicle } from '~/contracts';
import { getPriceDropValue } from '~/hooks/usePriceDrop';
import replaceUndefinedWithNull from '~/utilities/replaceUndefinedWithNull';

export function getVehicleData(latestVehicles: Partial<Vehicle>[]) {
  return latestVehicles.map((v: Partial<Vehicle>, tileIndex) => {
    const { apr, discounts, monthlyPayment, pricingErrorMessage, term, total } =
      v.price ?? {};
    const downPayment = v.price?.downPaymentDetails?.totalCashDue;

    const {
      deliveryInfo,
      isOnDemand,
      isPurchasePending,
      locationId,
      make,
      matchedFilters,
      mileage,
      model,
      pickupInfo,
      stockNumber,
      transportCost,
      trim,
      vehicleId,
      vehicleInventoryType,
      vehicleLockType,
      vehiclePurchaseType,
      vehicleUnlockDateTime,
      vehicleTags,
      vin,
      year
    } = v;

    const hasTerms = downPayment != null && monthlyPayment != null;

    const { activityType, dateAvailable, getItBy, routeId } =
      getDeliveryOrPickupByVehicle(v);

    if (
      (deliveryInfo?.activityType || pickupInfo?.activityType) &&
      !activityType
    ) {
      datadogRum.addAction(
        'null-tile-info-activityType-with-valid-delivery-or-pickup',
        {
          deliveryInfo,
          pickupInfo,
          tileInfoActivityType: activityType
        }
      );
    }

    return replaceUndefinedWithNull({
      deliveryInfo,
      isGreatDeal: maybeHasGreatDealTag(v),
      isOnDemand,
      isPriceDrop: getPriceDropValue(v) > 0,
      isPurchasePending,
      locationId,
      make,
      matchedFilters,
      mileage,
      model,
      pickupInfo,
      stockNumber,
      tileIndex,
      tileInfo: {
        activityType,
        apr,
        discounts,
        downPayment,
        getItBy,
        getItByDateIso: dateAvailable,
        hasTerms,
        monthlyPayment,
        pricingErrorMessage,
        routeId,
        shippingCost: transportCost,
        stickerPrice: total,
        term
      },
      trim,
      vehicleId,
      vehicleInventoryType,
      vehicleLockType,
      vehiclePurchaseType,
      vehicleUnlockDateTime,
      vehicleTags,
      vin,
      year
    });
  });
}

type OwnProps = {
  latestInventoryVehicles: Vehicle[];
  latestSimilarVehicles: Partial<Vehicle>[];
  shouldHaveSimilarVehicles: boolean;
};

export function useGetVehiclesLoaded({
  latestInventoryVehicles,
  latestSimilarVehicles,
  shouldHaveSimilarVehicles
}: OwnProps) {
  const { availabilityError, lowAvailabilityError, pricingError } =
    useResponseErrors();

  const userDeliveryInfo = useUserDeliveryInfo();
  const userDeliveryInfoRef = useRef(userDeliveryInfo);

  useEffect(() => {
    userDeliveryInfoRef.current = userDeliveryInfo;
  }, [userDeliveryInfo]);

  return useMemo(() => {
    const inventoryVehicles = getVehicleData(latestInventoryVehicles);

    const hasPricing =
      Boolean(pricingError) ||
      inventoryVehicles.some(
        v => v.tileInfo.hasTerms || v.tileInfo.pricingErrorMessage
      );

    const hasAvailability =
      userDeliveryInfoRef.current?.isOutOfMarket === true ||
      Boolean(availabilityError) ||
      inventoryVehicles.some(v => Boolean(v.pickupInfo || v.deliveryInfo));

    const inventoryVehiclesLoaded =
      inventoryVehicles.length === 0 || (hasPricing && hasAvailability);

    const similarVehiclesToSend = shouldHaveSimilarVehicles
      ? getVehicleData(latestSimilarVehicles)
      : [];

    const similarVehiclesAvailability =
      Boolean(lowAvailabilityError) ||
      similarVehiclesToSend.some(v => Boolean(v.pickupInfo || v.deliveryInfo));

    const similarVehiclesLoaded =
      similarVehiclesToSend.length > 0 &&
      similarVehiclesAvailability &&
      similarVehiclesToSend.some(
        v => v.tileInfo.hasTerms || v.tileInfo.pricingErrorMessage
      );

    return {
      loadedInventoryVehicles: {
        isLoaded: inventoryVehiclesLoaded,
        vehicles: inventoryVehicles
      },
      loadedSimilarVehicles: {
        isLoaded: similarVehiclesLoaded,
        vehicles: similarVehiclesToSend
      }
    };
  }, [
    availabilityError,
    latestInventoryVehicles,
    latestSimilarVehicles,
    lowAvailabilityError,
    pricingError,
    shouldHaveSimilarVehicles
  ]);
}
